import styled from 'styled-components'
import get from 'lodash/get'

import { useGetPhotosForListingQuery } from '../../../hooks/useQueries'

import HeartOnIcon from '../../icons/BucketlistHeartOn'
import HeartOffIcon from '../../icons/BucketlistHeartOff'
import { normalizeKeys } from '../../utils/propsUtils'

import CardSlider from '../shared/CardSlider'
import ListingDetails from '../shared/ListingDetails'
import DiscountBanner from '../shared/DiscountBanner'
import AvailableDates from '../shared/AvailableDates'
import { MediaWrapperOrientated } from '../shared/MediaWrapper'
import { CardWrapper, Content, BucketListButton } from '../shared/styled'

import { CamelCaseListing, SnakeCaseListing } from '../types'

export const ListingContent = styled(Content)`
  white-space: break-spaces;
`

type ListingCardProps = {
  listing: CamelCaseListing | SnakeCaseListing
  isInEscape?: boolean
  asLink?: boolean
  noBucketList?: boolean
  noGraphQL?: boolean
  onSelectEscapeClick?: () => any
  bucketListButtonLeft?: boolean
  compactCard?: boolean
  /** Eagerly load the cover image for above-the-fold SSR cards. */
  eager?: boolean
  /** Optionally pass in the photo to show as first/cover on the card */
  coverPhoto?: Pick<GQL_PhotoImage, 'medium' | 'large'>
  availableDates?: { startDate: string; endDate: string; total: number }[]
  showDriveTime?: boolean
}

const ListingCard = ({
  listing,
  asLink,
  noBucketList = false,
  isInEscape = false,
  onSelectEscapeClick = undefined,
  bucketListButtonLeft = false,
  noGraphQL = false,
  compactCard,
  eager = false,
  coverPhoto,
  availableDates,
  showDriveTime = false,
}: ListingCardProps) => {
  const {
    id,
    url,
    title,
    price,
    pricingSuffix,
    nicePricingSuffix,
    location,
    city,
    capacity,
    reviewScore,
    pricing,
    discount,
    photos,
    promoImage,
    instantBookEnabled,
    driveTimeMinutes,
    accessByBoat,
    subcategory,
  } = normalizeKeys(listing) as CamelCaseListing

  const { data: photoData } = useGetPhotosForListingQuery({
    variables: { id: id?.toString() },
    skip: noGraphQL,
    context: { batching: true },
  })

  // Fall back to promoImage so the cover is in the SSR HTML for crawlers.
  const galleryPhotos = get(photoData, 'photos', photos)
  const coverImage = coverPhoto || promoImage
  const sliderPhotos = galleryPhotos?.length
    ? [...(coverPhoto ? [{ image: coverPhoto }] : []), ...galleryPhotos]
    : [...(coverImage ? [{ image: coverImage }] : [])]

  return (
    <CardWrapper>
      <MediaWrapperOrientated>
        {sliderPhotos.length > 0 && (
          <CardSlider
            title={title}
            photos={sliderPhotos}
            url={url}
            asLink={asLink}
            eager={eager}
          />
        )}

        {!noBucketList && onSelectEscapeClick && (
          <BucketListButton
            onClick={onSelectEscapeClick}
            isActive={isInEscape}
            left={bucketListButtonLeft}
          >
            <HeartOnIcon />
            <HeartOffIcon />
          </BucketListButton>
        )}
      </MediaWrapperOrientated>

      <DiscountBanner discount={discount} />

      <ListingContent compact={compactCard}>
        <ListingDetails
          title={title}
          capacity={capacity}
          locationName={location?.name}
          reviewScore={reviewScore}
          subcategory={subcategory}
          city={city}
          driveTimeMinutes={driveTimeMinutes}
          accessByBoat={accessByBoat}
          showDriveTime={showDriveTime}
          price={price}
          pricing={pricing}
          pricingSuffix={pricingSuffix}
          nicePricingSuffix={nicePricingSuffix}
          instantBookEnabled={instantBookEnabled}
          compact={compactCard}
          linkOptions={{
            url,
            asLink,
          }}
        />
      </ListingContent>

      {availableDates?.length > 0 && (
        <div style={{ marginTop: '-24px', marginBottom: '32px' }}>
          <AvailableDates
            availableDates={availableDates}
            linkOptions={{ url, asLink }}
            thumbnail={
              coverPhoto?.medium || photoData?.photos?.[0]?.image?.medium
            }
            listingTitle={title}
            capacity={capacity}
            locationName={location?.name}
          />
        </div>
      )}
    </CardWrapper>
  )
}

export default ListingCard
